import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import DeepEqual from 'fast-deep-equal';
import SearchMenuItem from './SearchMenuItem';
import { tempRefs, INITIAL_STATE, MENU_ITEMS } from './constants';
import { analyticsTrack } from '../../../analyticsUtils';
import './SearchFilterMenu.scss';

const transformSelectionObjectData = (selection) => selection.map((sel) => {
  if (typeof sel === 'object') {
    return sel.label;
  }
  return sel;
});

const getSubmenuData = ({
  therapeuticAreas, industries, productTechnologies, developmentPhases,
  yearsOfExperience, hourlyRate, seniorityLevels, roles,
}) => (
  {
    Role: {
      Role: roles,
    },
    Expertise: {
      'Functional Area': [{
        value: 1,
        label: 'Clinical Operations',
      }],
      'Therapeutic Areas': therapeuticAreas,
      'Product Technologies': productTechnologies,
      Diseases: [],
      'Phases of Development': developmentPhases,
      Industries: industries,
      Keyword: null,
    },
    Seniority: {
      'Years of Experience': yearsOfExperience,
      'Seniority Level': seniorityLevels,
    },
    'Work Preferences': {
      'Hourly Rate': hourlyRate,
      Availability: false,
      // Location: location,
      Languages: [],
    },
  }
);

const doesStateHaveSelection = (selections) => !(DeepEqual(INITIAL_STATE.selections, selections));

class SearchFilterMenu extends PureComponent {
  constructor(props) {
    super(props);

    this.state = INITIAL_STATE;
    this.sliderResetFunctions = [];
    this.data = getSubmenuData(props);
    this.handleChange = this.handleChange.bind(this);
    this.handleSelection = this.handleSelection.bind(this);
    this.removeSelection = this.removeSelection.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
    this.handlestateRepopulation = this.handleStateRepopulation.bind(this);
    this.registerSliderResetFunction = this.registerSliderResetFunction.bind(this);
  }

  componentDidMount() {
    const { params } = this.props;
    const hasParams = Object.keys(params).length > 2;

    if (hasParams) {
      this.handleStateRepopulation();
    }
  }

  handleStateRepopulation() {
    const { params } = this.props;
    let stateCopy = this.state;
    const paramValues = tempRefs.reduce((prev, curr) => {
      if (curr.name === 'Keyword') {
        return [...prev, {
          name: curr.name,
          parent: curr.parent,
          value: params[curr.name] || null,
        }];
      }

      return [...prev, {
        name: curr.name,
        parent: curr.parent,
        value: params[curr.name] ? JSON.parse(params[curr.name]) : [],
      }];
    }, []);

    for (let i = 0, ii = paramValues.length; i < ii; i += 1) {
      stateCopy = {
        ...stateCopy,
        ...paramValues[i].parent ? {
          selections: {
            ...stateCopy.selections,
            [paramValues[i].parent]: {
              ...stateCopy.selections[paramValues[i].parent],
              [paramValues[i].name]: paramValues[i].value,
            },
          },
        } : {
          selections: {
            ...stateCopy.selections,
            [paramValues[i].name]: paramValues[i].value,
          },
        },
      };
    }
    this.setState(stateCopy);
  }

  handleChange(menuTitle, submenuTitle, value) {
    const { selections } = this.state;
    const { setSelectionState } = this.props;
    const newState = {
      selections: {
        ...selections,
        [menuTitle]: {
          ...selections[menuTitle],
          [submenuTitle]: value,
        },
      },
    };
    this.setState(newState, () => {
      if (submenuTitle !== 'Keyword') setSelectionState(newState.selections);
    });
  }

  handleSelection(menuTitle, selection, subsection) {
    const { setSelectionState } = this.props;
    const { selections } = this.state;
    let doesSelectionExist;

    analyticsTrack(`Filtered by ${subsection}`, { location: 'Search Index' });

    if (Array.isArray(selection)) {
      this.setState({
        ...subsection ? {
          selections: {
            ...selections,
            [menuTitle]: {
              ...selections[menuTitle],
              [subsection]: transformSelectionObjectData(selection),
            },
          },
        } : {
          selections: {
            ...selections,
            [menuTitle]: transformSelectionObjectData(selection),
          },
        },
      }, () => setSelectionState(this.state.selections));
      return;
    }

    if (subsection) {
      doesSelectionExist = selections[menuTitle][subsection].includes(selection);
      if (!doesSelectionExist) {
        this.setState({
          selections: {
            ...selections,
            [menuTitle]: {
              ...selections[menuTitle],
              [subsection]: [...selections[menuTitle][subsection], selection],
            },
          },
        }, () => setSelectionState(this.state.selections));
        return;
      }
      this.removeSelection(menuTitle, selection, subsection);
      return;
    }

    doesSelectionExist = selections[menuTitle].includes(selection);

    if (!doesSelectionExist) {
      this.setState({
        selections: {
          ...selections,
          [menuTitle]: [...selections[menuTitle], selection],
        },
      }, () => setSelectionState(this.state.selections));
      return;
    }
    this.removeSelection(menuTitle, selection);
  }

  removeSelection(menuTitle, _selection, subsection) {
    const { setSelectionState } = this.props;
    const { selections } = this.state;

    this.setState({
      ...subsection ? {
        selections: {
          ...selections,
          [menuTitle]: {
            ...selections[menuTitle],
            [subsection]: Array.isArray(_selection) ? []
              : selections[menuTitle][subsection].filter((selection) => {
                if (typeof _selection === 'object') {
                  return selection.label !== _selection.label;
                }
                return selection !== _selection;
              }),
          },
        },
      } : {
        selections: {
          ...selections,
          [menuTitle]: selections[menuTitle].filter((selection) => {
            if (typeof _selection === 'object') {
              return selection.label !== _selection.label;
            }
            return selection !== _selection;
          }),
        },
      },
    }, () => setSelectionState(this.state.selections));
  }

  registerSliderResetFunction(func) {
    this.sliderResetFunctions.push(func);
  }

  clearFilters() {
    const { clearParams } = this.props;
    this.setState(INITIAL_STATE, () => {
      clearParams();
      this.sliderResetFunctions.forEach((func) => func());
      analyticsTrack('Clicked Clear Filters', { location: 'Search Index' });
    });
  }

  render() {
    const { selections } = this.state;
    const { setSelectionState } = this.props;
    const hasSelection = doesStateHaveSelection(selections);

    return (
      <div className="search-filter-menu">
        <div className="header">
          <i className="fas fa-filter" />
          Filter by:
          {hasSelection
            && (
            <span
              className="body-text-2 demibold clear-filters-top"
              role="link"
              tabIndex={0}
              onClick={this.clearFilters}
            >
              Clear All
            </span>
            )}
        </div>
        <div className="menu-divider" />
        {
          MENU_ITEMS.map((name) => (
            <Fragment key={name}>
              <SearchMenuItem
                name={name}
                structuralData={this.data[name]}
                selectionData={selections}
                handleSelection={this.handleSelection}
                removeSelection={this.removeSelection}
                handleChange={this.handleChange}
                setSelectionState={setSelectionState}
                registerSliderResetFunction={this.registerSliderResetFunction}
              />
              <div className="item-divider" />
            </Fragment>
          ))
        }
        <div
          className="clear-filters-footer"
          role="button"
          tabIndex={0}
          onClick={this.clearFilters}
        >
          Clear Filters
        </div>
      </div>
    );
  }
}

SearchFilterMenu.propTypes = {
  params: PropTypes.shape({}).isRequired,
  setSelectionState: PropTypes.func.isRequired,
  clearParams: PropTypes.func.isRequired,
};

export default SearchFilterMenu;
